import { NavLink, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, FileText, Sparkles,
  ShoppingCart, Users, Package, Tag, Bell, ShieldAlert, Award, TrendingUp,
  LogOut, KeyRound, Wallet, Layers, Menu, X, Building2, TrendingDown, DollarSign,
  PiggyBank, Archive, Shield, Gift, Scale, Settings, Share2, RefreshCw,
} from "lucide-react";
import ChangePasswordModal from "@/components/ChangePasswordModal";
import CompartirCatalogoDialog from "@/components/CompartirCatalogoDialog";
import CriticalNotificationsGate from "@/components/CriticalNotificationsGate";
import TopBar from "@/components/TopBar";
import OfflineIndicator from "@/components/OfflineIndicator";
import BottomNav from "@/components/BottomNav";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

/**
 * Beem AppShell — Icon Rail Layout.
 *
 * Layout único y compacto:
 *   [rail iconos 68px] [contenido con topbar sticky arriba]
 *
 * - Sidebar es solo iconos con tooltip on hover — se siente workspace, no template.
 * - TopBar sticky con búsqueda Cmd+K, campanita, actividad del equipo, tema.
 * - Cada icono del rail tiene indicador circular animado si es la página activa.
 */
export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const [changeOpen, setChangeOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const location = useLocation();
  const isAdmin = user?.role === "admin";
  const initials = (user?.name || user?.email || "B").slice(0, 1).toUpperCase();

  // Cerrar drawer al cambiar de página
  useEffect(() => { setMobileNavOpen(false); }, [location.pathname]);

  // Helper: verifica si el usuario tiene alguno de los permisos listados.
  // Directores pasan siempre.
  const perms = user?.permissions || [];
  const hasAny = (list) => {
    if (!list || list.length === 0) return true;
    if (isAdmin || perms.includes("permisos.administrar")) return true;
    return list.some((p) => perms.includes(p));
  };

  // ---- Grupos de navegación (Mar 11, 2026) ----
  // Consolidamos los hubs de Pedidos y Productos con tabs internos para reducir
  // el ruido lateral. Remisiones, Pagos, Director Comercial y Reportes viven
  // dentro de /pedidos. Bajo stock vive dentro de /productos.
  const adminGroups = [
    {
      label: "Operación",
      items: [
        { to: "/", label: "Panel", icon: LayoutDashboard, testid: "nav-panel" },
        { to: "/pedidos", label: "Pedidos", icon: ShoppingCart, testid: "nav-pedidos" },
        { to: "/productos", label: "Productos", icon: Package, testid: "nav-productos" },
      ],
    },
    {
      label: "Personas",
      items: [
        { to: "/clientes", label: "Clientes", icon: Users, testid: "nav-clientes" },
      ],
    },
    {
      label: "Finanzas",
      items: [
        { to: "/reglas", label: "Reglas del negocio", icon: Scale, testid: "nav-reglas", adminOnly: true },
        { to: "/liquidacion-comisiones", label: "Liquidación comisiones", icon: DollarSign, testid: "nav-liquidacion" },
        { to: "/finanzas", label: "Finanzas", icon: Wallet, testid: "nav-finanzas", adminOnly: true },
      ],
    },
    {
      label: "Actividad",
      items: [
        { to: "/notificaciones", label: "Notificaciones", icon: Bell, testid: "nav-notificaciones" },
        { to: "/configuracion", label: "Configuración", icon: Settings, testid: "nav-configuracion", adminOnly: true },
      ],
    },
  ];

  const vendedorGroups = [
    {
      label: "Anclados",
      items: [
        { to: "/", label: "Mi panel", icon: LayoutDashboard, testid: "nav-mi-panel" },
        { to: "/pedidos", label: "Mis pedidos", icon: ShoppingCart, testid: "nav-mis-pedidos" },
        { to: "/productos", label: "Productos", icon: Package, testid: "nav-mis-productos" },
        { to: "/clientes", label: "Clientes", icon: Users, testid: "nav-mis-clientes",
          anyOf: ["clientes.ver", "clientes.crear", "clientes.editar", "ventas.crear", "ventas.ver_todos"] },
      ],
    },
    {
      label: "Reglas",
      items: [
        { to: "/reglas?tab=excepciones", label: "Excepciones", icon: ShieldAlert, testid: "nav-mis-exc",
          anyOf: ["excepciones.aprobar"] },
      ],
    },
    {
      label: "Comisiones",
      items: [
        { to: "/mis-comisiones", label: "Mis comisiones", icon: Award, testid: "nav-mis-comisiones",
          anyOf: ["ventas.crear", "comisiones.ver"] },
      ],
    },
    {
      label: "Actividad",
      items: [
        { to: "/notificaciones", label: "Notificaciones", icon: Bell, testid: "nav-mis-notif" },
      ],
    },
  ];

  // Filtrar items por permisos (director bypasea) y remover grupos vacíos.
  const filterGroups = (groups) => groups
    .map((g) => ({ ...g, items: g.items.filter((it) => hasAny(it.anyOf)) }))
    .filter((g) => g.items.length > 0);

  const activeGroups = filterGroups(isAdmin ? adminGroups : vendedorGroups);

  return (
    <TooltipProvider delayDuration={120} skipDelayDuration={80}>
    <div className="relative flex h-screen w-full overflow-hidden bg-background text-foreground">
      {/* Watermark floral gigante — signature del brand */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-40 -right-40 w-[780px] h-[780px] opacity-[0.06] dark:opacity-[0.08] blur-[2px]"
        style={{
          backgroundImage: 'url(/beem-logo.jpg)',
          backgroundSize: 'contain',
          backgroundRepeat: 'no-repeat',
          transform: 'rotate(18deg)',
        }}
      />

      {/* ---- Icon rail (68px) ---- */}
      <aside className="relative z-20 hidden md:flex flex-col w-[68px] py-3 items-center border-r border-border/50 bg-nude/40 backdrop-blur-md">
        {/* Logo */}
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="relative w-11 h-11 rounded-2xl overflow-hidden ring-1 ring-primary/25 shadow-rose-glow mb-2 cursor-default">
              <img src="/beem-logo.jpg" alt="Beem" className="w-full h-full object-cover" />
            </div>
          </TooltipTrigger>
          <TooltipContent side="right" sideOffset={10} className="bg-foreground text-background border-0">
            <span className="font-display italic text-[13px]">beem</span> · Importaciones
          </TooltipContent>
        </Tooltip>

        <div className="w-8 h-px bg-border/60 mb-2" />

        {/* Nav icons */}
        {/* Nav icons — agrupados por función con divider y micro-label */}
        <nav className="flex-1 flex flex-col items-center overflow-y-auto no-scrollbar w-full px-2 gap-2 py-1">
          {activeGroups.map((group, gi) => (
            <div key={group.label} className="flex flex-col items-center w-full">
              {gi > 0 && <div className="w-7 h-px bg-border/50 my-1" aria-hidden />}
              <div
                className="text-[8.5px] font-bold uppercase tracking-[0.14em] text-muted-foreground/55 mb-1 select-none"
                data-testid={`nav-group-${group.label.toLowerCase()}`}
              >
                {group.label}
              </div>
              <div className="flex flex-col items-center gap-1 w-full">
                {group.items.map(({ to, label, icon: Icon, testid }) => (
                  <Tooltip key={to}>
                    <TooltipTrigger asChild>
                      <NavLink
                        to={to} end={to === "/"} data-testid={testid}
                        className={({ isActive }) =>
                          `group relative flex items-center justify-center w-11 h-11 rounded-2xl transition-all duration-200 ease-out ${
                            isActive
                              ? "bg-primary/12 text-primary shadow-inner-highlight"
                              : "text-foreground/60 hover:text-foreground hover:bg-accent/50"
                          }`
                        }
                      >
                        {({ isActive }) => (
                          <>
                            {isActive && (
                              <span className="absolute -left-2 top-1/2 -translate-y-1/2 w-1 h-6 rounded-r-full bg-primary" />
                            )}
                            <Icon className="w-[19px] h-[19px]" strokeWidth={isActive ? 2.2 : 1.7} />
                          </>
                        )}
                      </NavLink>
                    </TooltipTrigger>
                    <TooltipContent side="right" sideOffset={10} className="bg-foreground text-background border-0 font-medium">
                      {label}
                    </TooltipContent>
                  </Tooltip>
                ))}
              </div>
            </div>
          ))}
        </nav>

        {/* Footer: user avatar + actions */}
        <div className="mt-2 flex flex-col items-center gap-1 pb-1">
          <div className="w-8 h-px bg-border/60 mb-1" />
          <Tooltip>
            <TooltipTrigger asChild>
              <button data-testid="share-catalogo-btn" onClick={() => setShareOpen(true)}
                className="relative w-11 h-11 rounded-2xl flex items-center justify-center text-primary hover:bg-primary/10 transition-all">
                <Share2 className="w-[17px] h-[17px]" strokeWidth={1.8} />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right" sideOffset={10} className="bg-foreground text-background border-0 font-medium">Compartir mi catálogo</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <button data-testid="change-password-btn" onClick={() => setChangeOpen(true)}
                className="relative w-11 h-11 rounded-2xl flex items-center justify-center text-foreground/60 hover:text-foreground hover:bg-accent/50 transition-all">
                <KeyRound className="w-[17px] h-[17px]" strokeWidth={1.8} />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right" sideOffset={10} className="bg-foreground text-background border-0 font-medium">Contraseña</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <button data-testid="logout-btn" onClick={logout}
                className="relative w-11 h-11 rounded-2xl flex items-center justify-center text-foreground/60 hover:text-destructive hover:bg-destructive/10 transition-all">
                <LogOut className="w-[17px] h-[17px]" strokeWidth={1.8} />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right" sideOffset={10} className="bg-foreground text-background border-0 font-medium">Salir</TooltipContent>
          </Tooltip>
          {/* User chip */}
          <div className="relative w-9 h-9 rounded-full bg-gradient-to-br from-primary/25 to-secondary/25 text-primary flex items-center justify-center font-semibold text-[13px] font-display italic mt-1 border-2 border-nude/70" title={`${user?.name || "Beem"} · ${isAdmin ? "Directora" : "Vendedora"}`}>
            {initials}
          </div>
        </div>
      </aside>

      {/* ---- Main pane ---- */}
      <div className="relative z-10 flex-1 flex flex-col overflow-hidden min-w-0">
        <TopBar onOpenMobileNav={() => setMobileNavOpen(true)} />
        <main className="flex-1 overflow-y-auto">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-10 py-6 md:py-8 pb-24 md:pb-8">
            {children}
          </div>
        </main>
      </div>

      <BottomNav isAdmin={isAdmin} onOpenDrawer={() => setMobileNavOpen(true)} hidden={location.pathname === "/pedidos/movil"} />

      {/* ---- Drawer móvil (nav completo) ---- */}
      {mobileNavOpen && (
        <div className="fixed inset-0 z-[70] md:hidden">
          <div className="absolute inset-0 bg-foreground/40 backdrop-blur-sm" onClick={() => setMobileNavOpen(false)} data-testid="mobile-nav-overlay" />
          <aside className="absolute inset-y-0 left-0 w-[78%] max-w-[300px] bg-nude/95 backdrop-blur-xl border-r border-border/60 shadow-soft-lift flex flex-col animate-slide-in-left">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border/50">
              <div className="flex items-center gap-2.5">
                <div className="w-10 h-10 rounded-2xl overflow-hidden ring-1 ring-primary/25">
                  <img src="/beem-logo.jpg" alt="Beem" className="w-full h-full object-cover" />
                </div>
                <div>
                  <div className="text-sm font-display italic text-primary">beem · Importaciones</div>
                </div>
              </div>
              <button onClick={() => setMobileNavOpen(false)} className="w-9 h-9 rounded-full hover:bg-accent flex items-center justify-center text-foreground/70" data-testid="mobile-nav-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto py-3 px-2">
              {activeGroups.map((group, gi) => (
                <div key={group.label} className={gi > 0 ? "mt-3" : ""}>
                  <div className="px-3 pb-1.5 pt-1 text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground/70">
                    {group.label}
                  </div>
                  {group.items.map(({ to, label, icon: Icon, testid }) => (
                    <NavLink
                      key={to} to={to} end={to === "/"} data-testid={`mob-${testid}`}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm mb-0.5 transition-colors ${
                          isActive
                            ? "bg-primary/12 text-primary font-semibold"
                            : "text-foreground/75 hover:bg-accent/50"
                        }`
                      }
                    >
                      <Icon className="w-[19px] h-[19px] flex-shrink-0" strokeWidth={1.8} />
                      <span className="truncate">{label}</span>
                    </NavLink>
                  ))}
                </div>
              ))}
            </nav>
            <div className="border-t border-border/50 py-2 px-2 space-y-1">
              <div className="flex items-center gap-3 px-3 py-2">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary/25 to-secondary/25 text-primary flex items-center justify-center font-semibold text-[13px] font-display italic border-2 border-nude/70">
                  {initials}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-foreground truncate">{user?.name || "—"}</div>
                  <div className="text-[11px] text-muted-foreground truncate">{isAdmin ? "Directora" : "Vendedora"}</div>
                </div>
              </div>
              <button onClick={() => { setMobileNavOpen(false); setShareOpen(true); }} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-primary hover:bg-primary/10" data-testid="mob-share-catalogo-btn">
                <Share2 className="w-4 h-4" /> Compartir mi catálogo
              </button>
              <button onClick={() => { setMobileNavOpen(false); setChangeOpen(true); }} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-foreground/75 hover:bg-accent/50" data-testid="mob-change-password-btn">
                <KeyRound className="w-4 h-4" /> Cambiar contraseña
              </button>
              <button
                onClick={async () => {
                  // Feb/2026: ESCAPE HATCH para caché PWA pegado. Desregistra
                  // TODOS los Service Workers, borra todos los caches y recarga
                  // sin usar caché. El usuario toca este botón cuando "aún me
                  // sale la versión vieja" tras un deploy.
                  try {
                    if ("serviceWorker" in navigator) {
                      const regs = await navigator.serviceWorker.getRegistrations();
                      await Promise.all(regs.map((r) => r.unregister()));
                    }
                    if ("caches" in window) {
                      const keys = await caches.keys();
                      await Promise.all(keys.map((k) => caches.delete(k)));
                    }
                  } catch (e) { console.warn("Force update cleanup failed", e); }
                  // Reload sin caché del navegador
                  window.location.reload(true);
                }}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-foreground/75 hover:bg-accent/50"
                data-testid="mob-force-update-btn"
                title="Fuerza la descarga de la última versión. Útil si tras un cambio aún ves la versión vieja."
              >
                <RefreshCw className="w-4 h-4" /> Actualizar app
              </button>
              <button onClick={logout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-destructive hover:bg-destructive/10" data-testid="mob-logout-btn">
                <LogOut className="w-4 h-4" /> Salir
              </button>
            </div>
          </aside>
        </div>
      )}

      <ChangePasswordModal open={changeOpen} onClose={() => setChangeOpen(false)} />
      <CompartirCatalogoDialog open={shareOpen} onOpenChange={setShareOpen} />
      <CriticalNotificationsGate />
      <OfflineIndicator />

      <style>{`.no-scrollbar::-webkit-scrollbar { display: none; } .no-scrollbar { scrollbar-width: none; } @keyframes beem-slide-in-left { from { transform: translateX(-100%); } to { transform: translateX(0); } } .animate-slide-in-left { animation: beem-slide-in-left 220ms cubic-bezier(0.32, 0.72, 0, 1); }`}</style>
    </div>
    </TooltipProvider>
  );
}
